import { createContext, type ReactNode, useContext, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { RENTER_INVITE_QUERY_PARAM } from '@/constants/Constants';
import { UserPermissions } from '@/constants/permissions.constant';
import { usePermission } from '@/hooks/usePermission';
import { InviteLeaseDetailsDrawer } from './invite-lease-details.drawer';

type NewDealHighlight = {
  /** Email of the applicant just invited, so the Applications list can highlight their row. */
  newDealEmail: string | null;
  setNewDealEmail: (email: string | null) => void;
};

const NewDealHighlightContext = createContext<NewDealHighlight>({
  newDealEmail: null,
  setNewDealEmail: () => {},
});

export const useNewDealEmail = () => useContext(NewDealHighlightContext);

/**
 * Hosts the invite drawer on every page. `?renter-invite=true` is the drawer's only open state, so
 * links, Back and closing all go through the URL.
 */
export function InviteDrawerHost({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const [searchParams] = useSearchParams();
  const { notSet, enabled: canInvite } = usePermission(UserPermissions.Invite.CanInviteRenter);
  const isOpen =
    enabled && (notSet || canInvite) && searchParams.get(RENTER_INVITE_QUERY_PARAM) === 'true';

  const [newDealEmail, setNewDealEmail] = useState<string | null>(null);
  const highlight = useMemo(() => ({ newDealEmail, setNewDealEmail }), [newDealEmail]);

  return (
    <NewDealHighlightContext.Provider value={highlight}>
      {children}
      <InviteLeaseDetailsDrawer isOpen={isOpen} />
    </NewDealHighlightContext.Provider>
  );
}
